// 待宵の花嫁「ヴェールの招待」(veilInvitation・床の帯 kind 'veil') の見た目 — 影の手 (2026-10-20)。
//
// 作者の選択 = 床技7つの見本の C「影の手」(docs/research/2026-10-19-floor-fx-mihon.md §5・台帳 production.md 10-19 夜)。
//   「ヴェールの裾の形の影の手が床から伸び、指を閉じて掴む」。作者の物差し「名前通り作りたい」
//   = 花嫁がヴェールの向こうへ招く → 床の影から手が伸びて、来た者を掴んで引き込む。
// 段:
//   ① 予告 (850ms): 帯の中の床に影だまりが広がり、縁に菫のひびが走る。後半は指先が床から覗いてもがく。
//   ② 当たり 0ms: 腕が一気に伸び上がる (約4コマ・少し行き過ぎて戻る)。指を開いて掴みかかる。
//   ③ 当たり 90〜220ms: 手が順に指を閉じて握り、少し下へ引く (掴む)。
//   ④ 当たりの終わり〜後: 腕が崩れて影のかけらとヴェールの切れ端になって舞い、影だまりが引いて晴れる。
// 絵の部品 = Codex が1枚に描いた物を切り出した atlas (元絵 そざい/concept/bride-veil-hands-1020/・
//   切り出しの記録は LUNAIRE-notes/assets/2026-10-20-bride-veil-hands/)。動きはコード。
// 描くだけ。当たり (場所・時間・ダメージ) は変えない。絵の基準点は全部 帯の ±V_BLEED の中。
// 色: 菫は花嫁の弾けない合図 (今の壁の根元の菫と同じ swc('unparry', …))。赤・金・白い線は使わない。
// 前の配布版 (婚礼ヴェールの壁 drawVeilWall・render.ts) は DEV `?brideVeilFx=wall`。
import { VEIL_TSUNAMI_KINDS, type VeilTsunamiKind } from './bride_veil_tsunami'
import { VEIL_RISE_KINDS, type VeilRiseKind } from './bride_veil_rise'
import { isVeilSidesKind, type VeilSidesKind } from './bride_veil_sides'
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'
import { sw as swc } from './purple_swap'

// cloth / cloth2 = 布のヴェールの比較版 (2026-10-23・src/bride_veil_cloth.ts・DEV だけ)。
// rise / rise-topple / rise-cloth = 床から生えて重力で落ちるヴェール (2026-10-23・src/bride_veil_rise.ts・DEV だけ)。
// wall-cloth = 前の壁の出方と波打ち方のまま一枚布にした版 (2026-10-23・src/bride_veil_wallcloth.ts・DEV だけ)。
// sides = 両側から津波のように押し寄せ、波頭が内へ巻いて覆いかぶさるヴェール (2026-10-23・src/bride_veil_sides.ts・DEV だけ)。
// sides-tulle = sides と同じ動きで、貼る絵だけ透けるヴェールの大きな一枚布 (片側3枚) にした版 (2026-10-23・DEV だけ)。
// hands-veil = 影の手と同じ作り方 (同じ配置・時間・動きのコード) で、部品の絵だけヴェールの布にした版 (2026-10-23・DEV だけ)。
export type BrideVeilFx = 'hands' | 'hands-veil' | VeilSidesKind | 'wall' | 'cloth' | 'cloth2' | 'wall-cloth' | VeilTsunamiKind | VeilRiseKind
let veilFx: BrideVeilFx = 'hands'

/** 影の手の仕組みで描く版か (hands・hands-veil)。 */
export function isVeilHandsFx(v: BrideVeilFx): boolean { return v === 'hands' || v === 'hands-veil' }

/** game.ts の DEV URL ブロックだけが呼ぶ。指定なし (null)・知らない値は採用済みの既定 (影の手)。 */
export function setBrideVeilFx(v: string | null): void {
  veilFx = v === 'wall' ? 'wall'
    : import.meta.env.DEV && (v === 'cloth' || v === 'cloth2' || v === 'wall-cloth' || v === 'hands-veil' || isVeilSidesKind(v ?? '')) ? v as BrideVeilFx
    : import.meta.env.DEV && (VEIL_TSUNAMI_KINDS as readonly string[]).includes(v ?? '') ? v as VeilTsunamiKind
    : import.meta.env.DEV && (VEIL_RISE_KINDS as readonly string[]).includes(v ?? '') ? v as VeilRiseKind
    : 'hands'
}
export function getBrideVeilFx(): BrideVeilFx { return veilFx }

// ---------------------------------------------------------------- 部品の絵

type Rect = readonly [number, number, number, number]
// 名前: open_i / fist_i (同じ腕の開いた手と握った手・i=0..3)・pool_i・shard_i・lace_i。
export const HANDS_ATLAS: Record<string, Rect> = {
  open_0: [0, 0, 97, 185],
  fist_0: [99, 0, 97, 158],
  open_1: [198, 0, 108, 174],
  fist_1: [308, 0, 108, 160],
  open_2: [0, 187, 108, 181],
  fist_2: [110, 187, 108, 146],
  open_3: [220, 187, 101, 188],
  fist_3: [323, 187, 101, 165],
  pool_0: [0, 377, 206, 87],
  pool_1: [208, 377, 151, 80],
  pool_2: [361, 377, 120, 71],
  shard_0: [483, 377, 29, 15],
  shard_1: [0, 466, 16, 16],
  shard_2: [18, 466, 24, 18],
  shard_3: [44, 466, 27, 19],
  shard_4: [73, 466, 35, 38],
  shard_5: [110, 466, 20, 27],
  shard_6: [132, 466, 18, 35],
  shard_7: [152, 466, 14, 18],
  shard_8: [168, 466, 24, 30],
  shard_9: [194, 466, 26, 27],
  shard_10: [222, 466, 24, 21],
  shard_11: [248, 466, 17, 20],
  shard_12: [267, 466, 19, 15],
  shard_13: [288, 466, 8, 13],
  lace_0: [298, 466, 53, 69],
  lace_1: [353, 466, 29, 70],
  lace_2: [384, 466, 68, 61],
  lace_3: [0, 538, 62, 59],
}
const HANDS_SRC = '/sprites/fx/hazard/bride_veil_hands_atlas.png'
let handsImg: CanvasImageSource | null = null
let handsLoading = false
/** 部品の絵の読み込み (1回)。 */
export function preloadBrideVeilHandsAtlas(): void {
  if (handsImg || handsLoading || typeof Image === 'undefined') return
  handsLoading = true
  const img = new Image()
  img.onload = () => { handsImg = img }
  img.onerror = () => { handsLoading = false }
  img.src = HANDS_SRC
}
/** テスト用 (偽の絵を差す)。 */
export function setBrideVeilHandsAtlasForTest(img: CanvasImageSource | null): void { handsImg = img }

// hands-veil の部品 (動きは影の手と同じ名前で引く: open_i=広がったヴェール / fist_i=閉じて細く垂れたヴェール・pool_i・shard_i・lace_i)。
// 10-23 作り直し: 手を手本にせず、花嫁のヴェールだけを手本に Codex が描いた布 (描き直し0回)。open/fist は床の根元を横の真ん中にそろえてある。
// 元絵と切り出し LUNAIRE-notes/assets/2026-10-23-bride-veil-cloth-art/ (tools/cut_veil.py)。
export const VEIL_PARTS_ATLAS: Record<string, Rect> = {
  open_0: [0, 0, 127, 185],
  open_1: [129, 0, 99, 181],
  open_2: [230, 0, 67, 185],
  open_3: [299, 0, 91, 175],
  fist_0: [392, 0, 50, 152],
  fist_1: [444, 0, 48, 155],
  fist_2: [0, 187, 40, 155],
  fist_3: [42, 187, 37, 152],
  pool_0: [81, 187, 182, 56],
  pool_1: [265, 187, 128, 51],
  pool_2: [395, 187, 91, 37],
  shard_0: [0, 344, 33, 39],
  shard_1: [35, 344, 32, 30],
  shard_2: [69, 344, 10, 30],
  shard_3: [81, 344, 22, 28],
  shard_4: [105, 344, 24, 21],
  shard_5: [131, 344, 28, 20],
  shard_6: [161, 344, 24, 18],
  shard_7: [187, 344, 21, 28],
  shard_8: [210, 344, 22, 30],
  shard_9: [234, 344, 27, 25],
  shard_10: [263, 344, 14, 20],
  shard_11: [279, 344, 18, 19],
  shard_12: [299, 344, 24, 23],
  shard_13: [325, 344, 11, 11],
  lace_0: [338, 344, 72, 27],
  lace_1: [412, 344, 73, 25],
  lace_2: [0, 385, 70, 23],
  lace_3: [72, 385, 58, 27],
}
const VEIL_PARTS_SRC = '/sprites/fx/hazard/bride_veil_hands_veil_atlas.png'
let veilPartsImg: CanvasImageSource | null = null
let veilPartsLoading = false
export function preloadBrideVeilHandsVeilAtlas(): void {
  if (veilPartsImg || veilPartsLoading || typeof Image === 'undefined') return
  veilPartsLoading = true
  const img = new Image()
  img.onload = () => { veilPartsImg = img }
  img.onerror = () => { veilPartsLoading = false }
  img.src = VEIL_PARTS_SRC
}
export function setBrideVeilHandsVeilAtlasForTest(img: CanvasImageSource | null): void { veilPartsImg = img }

// 絵と切り出し表の組 (影の手 / ヴェール)。数は表から数える (絵ごとにかけらの数が違う)。
type Skin = { img: CanvasImageSource; rects: Record<string, Rect>; shards: number; laces: number; pools: number }
function countOf(rects: Record<string, Rect>, head: string): number {
  return Object.keys(rects).filter((k) => k.startsWith(head)).length
}
function skinOf(rects: Record<string, Rect>, img: CanvasImageSource): Skin {
  return { img, rects, shards: countOf(rects, 'shard_'), laces: countOf(rects, 'lace_'), pools: countOf(rects, 'pool_') }
}
// 今の版の絵。まだ読めていなければ読み込みを始めて null。
function skinNow(): Skin | null {
  if (veilFx === 'hands-veil') {
    if (!veilPartsImg) { preloadBrideVeilHandsVeilAtlas(); return null }
    return skinOf(VEIL_PARTS_ATLAS, veilPartsImg)
  }
  if (!handsImg) { preloadBrideVeilHandsAtlas(); return null }
  return skinOf(HANDS_ATLAS, handsImg)
}

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
// 行き過ぎて戻る (最大 約1.08)
function easeOutBack(p: number): number {
  const x = clamp01(p) - 1
  const c = 1.5
  return 1 + (c + 1) * x * x * x + c * x * x
}

// 部品1つを、下端中央 (ax, ay) を基準に回して描く。dw×dh はゲームの px。
function part(
  ctx: CanvasRenderingContext2D, sk: Skin, name: string,
  ax: number, ay: number, dw: number, dh: number, rot: number, a: number, flip = false,
): void {
  if (a <= 0.01 || dw <= 0.5 || dh <= 0.5) return
  const r = sk.rects[name]
  if (!r) return
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(ax, ay)
  if (rot) ctx.rotate(rot)
  if (flip) ctx.scale(-1, 1)
  ctx.drawImage(sk.img, r[0], r[1], r[2], r[3], -dw / 2, -dh, dw, dh)
  ctx.restore()
}

// ---------------------------------------------------------------- 帯ごとの配置

const V_BLEED = 8            // 帯の左右へはみ出してよい幅 (部品の基準点)
const RISE_MS = 70           // 腕が伸び上がる時間 (当たりの 0ms から)
const GRAB_FROM_MS = 90      // 指を閉じ始める (手ごとに少しずつずらす)
const GRAB_MS = 70           // 開いた手 → 握った手 の入れ替え
const LINGER_MS = 460        // 当たりの後、かけらが舞って晴れるまで

type Hand = { idx: number; px: number; h: number; lean: number; flip: boolean; delay: number; seed: number }
type Geom = { xStart: number; xEnd: number; w: number; cx: number; seed: number; hands: Hand[] }

function geomOf(hz: ActiveHit): Geom {
  const w = hz.xEnd - hz.xStart
  const cx = (hz.xStart + hz.xEnd) / 2
  const seed = hz.id * 1.91 + 23.7
  // 手の数は帯の幅から (足元の帯 110px＝2本・奥の帯 270px＝4〜5本)。等間隔に並べない。
  const n = Math.max(2, Math.min(6, Math.round(w / 58)))
  const hands: Hand[] = []
  for (let i = 0; i < n; i += 1) {
    const s = seed + i * 13.7
    const u = (i + 0.2 + hash01(s) * 0.6) / n
    const px = hz.xStart + 10 + u * (w - 20)
    // 腕の背丈: 主人公の背丈 (約 95px) を越えるものと、腰ほどのものを混ぜる。
    const tall = (i + Math.floor(hash01(seed) * 2)) % 2 === 0
    const h = (tall ? 132 + hash01(s + 1) * 34 : 92 + hash01(s + 2) * 26)
    // 傾き: 帯の真ん中へ寄る向き (掴みかかる)。端の手ほど内へ倒す。
    const toward = Math.sign(cx - px) || (hash01(s + 3) < 0.5 ? -1 : 1)
    const lean = toward * (0.06 + hash01(s + 4) * 0.22)
    hands.push({ idx: (i + Math.floor(hash01(seed + 5) * 4)) % 4, px, h, lean, flip: hash01(s + 6) < 0.5, delay: hash01(s + 7) * 36 + i * 9, seed: s })
  }
  return { xStart: hz.xStart, xEnd: hz.xEnd, w, cx, seed, hands }
}

// ---------------------------------------------------------------- 描く

// 影だまり (床に平たく広がる・帯の中だけ)。open=0..1 で広がり、a で濃さ。
function drawPool(ctx: CanvasRenderingContext2D, sk: Skin, g: Geom, floorY: number, open: number, a: number, timeSec: number): void {
  if (a <= 0.01 || open <= 0.01) return
  const n = g.w > 160 ? 2 : 1
  for (let i = 0; i < n; i += 1) {
    const name = `pool_${(i + Math.floor(hash01(g.seed + 9) * 3)) % Math.max(1, sk.pools)}`
    const r = sk.rects[name]
    if (!r) continue
    const segW = (g.w / n) * (n > 1 ? 1.18 : 1.06) * open
    const px = g.xStart + (g.w / n) * (i + 0.5)
    const breathe = 1 + 0.06 * Math.sin(timeSec * 5.3 + i * 2.1)
    const segH = Math.min(r[3] * (segW / r[2]), 30) * breathe
    part(ctx, sk, name, px, floorY + 5, segW, segH, 0, a, i % 2 === 1)
  }
}

// 床の下からの菫の照り (今の壁の根元の菫と同じ色)。帯の中だけ。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, h: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 横長の楕円 (床の線が中心) で、帯の端に四角い縁を作らない (10-20 撮影で箱に見えた)。
  ctx.beginPath()
  ctx.rect(g.xStart - V_BLEED, floorY - h, g.w + V_BLEED * 2, h)
  ctx.clip()
  ctx.translate(g.cx, floorY)
  ctx.scale((g.w / 2 + V_BLEED) / h, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, h)
  gr.addColorStop(0, `rgba(${swc('unparry', '158,116,214')},${(0.42 * a).toFixed(3)})`)
  gr.addColorStop(0.6, `rgba(${swc('unparry', '150,110,204')},${(0.16 * a).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${swc('unparry', '150,110,204')},0)`)
  ctx.fillStyle = gr
  ctx.fillRect(-h, -h, h * 2, h)
  ctx.restore()
}

// 腕1本。up=伸び具合 (0=指先だけ床から覗く〜1=全部)、grab=0 開いた手〜1 握った手。
function drawArm(
  ctx: CanvasRenderingContext2D, sk: Skin, hd: Hand, floorY: number,
  up: number, grab: number, a: number, timeSec: number, sink = 0,
): void {
  const ro = sk.rects[`open_${hd.idx}`]
  if (!ro) return
  const dh = hd.h
  const dw = dh * (ro[2] / ro[3])
  // 床から上へ出ている長さ = dh * up。下へずらして、床より下は切る。
  const shown = dh * up
  const pull = grab * 6 + sink * dh
  const baseY = floorY + (dh - shown) + pull
  const sway = Math.sin(timeSec * 3.1 + hd.seed) * 0.035 * (1 - grab * 0.6)
  const rot = hd.lean * (0.55 + 0.45 * up) + sway
  ctx.save()
  ctx.beginPath()
  ctx.rect(hd.px - dw * 1.6, floorY - dh * 1.4, dw * 3.2, dh * 1.4 + 4)
  ctx.clip()
  if (grab < 1) part(ctx, sk, `open_${hd.idx}`, hd.px, baseY, dw, dh, rot, a * (1 - grab), hd.flip)
  if (grab > 0) {
    const rf = sk.rects[`fist_${hd.idx}`]!
    const fh = dh * (rf[3] / ro[3])
    const fw = fh * (rf[2] / rf[3])
    part(ctx, sk, `fist_${hd.idx}`, hd.px, baseY, fw, fh, rot, a * grab, hd.flip)
  }
  ctx.restore()
}

/** 予告 (telegraph)。絵が無ければ false (呼び手は前の壁で描く)。 */
export function drawVeilHandsTelegraph(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number): boolean {
  const sk = skinNow()
  if (!sk) return false
  const g = geomOf(hz)
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  const late = p >= 0.75
  const pulse = late ? 1 + 0.18 * Math.sin(timeSec * 14) : 1
  // 影だまり: 最初の 30% で帯いっぱいへ広がる。
  const open = easeOut(p / 0.3)
  underGlow(ctx, g, floorY, (0.35 + 0.55 * p) * pulse, 34 + 30 * p)
  drawPool(ctx, sk, g, floorY, open, 0.55 + 0.45 * smooth01(0, 0.4, p), timeSec)
  // 指先が覗く: 40% から。後半ほど高く・もがく。
  const peek = smooth01(0.4, 1, p)
  if (peek > 0) {
    for (const hd of g.hands) {
      const wob = 0.5 + 0.5 * Math.sin(timeSec * 9 + hd.seed * 3)
      const up = (0.06 + 0.1 * peek + 0.04 * wob * peek) * Math.min(1, 160 / hd.h)
      drawArm(ctx, sk, hd, floorY, up * smooth01(0, 0.25, peek + hash01(hd.seed) * 0.2), 0, 0.9, timeSec)
    }
  }
  return true
}

/** 当たり (active)。絵が無ければ false。 */
export function drawVeilHandsActive(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number): boolean {
  const sk = skinNow()
  if (!sk) return false
  const ms = hz.elapsedMs - hz.telegraphMs
  remember(hz, timeSec - ms / 1000)
  drawHandsAt(ctx, sk, geomOf(hz), floorY, ms, hz.activeMs, timeSec)
  return true
}

function drawHandsAt(
  ctx: CanvasRenderingContext2D, sk: Skin, g: Geom, floorY: number,
  ms: number, activeMs: number, timeSec: number,
): void {
  const end = activeMs
  // 影だまりは当たりの間は満ち、後で引く。
  const poolA = 1 - smooth01(end - 60, end + LINGER_MS * 0.8, ms)
  underGlow(ctx, g, floorY, (ms < 120 ? 1.15 - ms / 800 : 1) * poolA, 64)
  drawPool(ctx, sk, g, floorY, 1 - 0.35 * smooth01(end, end + LINGER_MS, ms), poolA, timeSec)
  for (const hd of g.hands) {
    const t = ms - hd.delay * 0.35
    // 予告の最後 (指先が覗いた高さ) から一気に伸びる。
    const start = 0.16 * Math.min(1, 160 / hd.h)
    const up = start + (1 - start) * easeOutBack(t / RISE_MS)
    const grab = smooth01(GRAB_FROM_MS + hd.delay, GRAB_FROM_MS + hd.delay + GRAB_MS, ms)
    // 崩れ: 当たりの終わり 110ms 前から沈み始め、終わりの後 140ms で消える。
    const crumble = smooth01(end - 110, end + 140, ms)
    const sink = 0.35 * smooth01(end - 60, end + 160, ms)
    if (crumble < 1) drawArm(ctx, sk, hd, floorY, Math.max(0.02, up), grab, 1 - crumble, timeSec, sink)
    drawShards(ctx, sk, g, hd, floorY, ms - (end - 110), timeSec)
  }
}

// 崩れた腕から舞う影のかけらとヴェールの切れ端 (t=崩れ始めからの ms)。帯の ±V_BLEED の中だけ。
function drawShards(ctx: CanvasRenderingContext2D, sk: Skin, g: Geom, hd: Hand, floorY: number, t: number, timeSec: number): void {
  if (t <= 0 || t > LINGER_MS + 160) return
  const n = hd.h > 120 ? 5 : 3
  for (let k = 0; k < n; k += 1) {
    const s = hd.seed + k * 7.31
    const born = hash01(s) * 90
    const life = 300 + hash01(s + 1) * 220
    const q = (t - born) / life
    if (q <= 0 || q >= 1) continue
    const lace = sk.laces > 0 && k === n - 1 && hash01(s + 2) < 0.6
    const name = lace ? `lace_${Math.floor(hash01(s + 3) * sk.laces)}` : `shard_${Math.floor(hash01(s + 4) * sk.shards)}`
    const r = sk.rects[name]
    if (!r) continue
    // 腕のどこから剥がれるか (高さ)。上へ漂い、横へ少し流れる。レースはゆっくり落ちる。
    const y0 = floorY - hd.h * (0.25 + 0.7 * hash01(s + 5))
    const vx = (hash01(s + 6) - 0.5) * 70
    const x = Math.max(g.xStart - V_BLEED, Math.min(g.xEnd + V_BLEED, hd.px + Math.sin(hd.lean) * (floorY - y0) + vx * q))
    const y = lace ? y0 + 30 * q * q : y0 - (34 + hash01(s + 7) * 40) * easeOut(q)
    const size = lace ? 15 + hash01(s + 8) * 8 : 7 + hash01(s + 8) * 9
    const dw = size
    const dh = size * (r[3] / r[2])
    const rot = (hash01(s + 9) - 0.5) * 2 + (hash01(s + 10) - 0.5) * 6 * q + Math.sin(timeSec * 4 + s) * 0.2
    const a = (1 - smooth01(0.55, 1, q)) * smooth01(0, 0.08, q)
    part(ctx, sk, name, x, y + dh / 2, dw, dh, rot, a, hash01(s + 11) < 0.5)
  }
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { g: Geom; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()
function remember(hz: ActiveHit, startSec: number): void {
  if (!traces.has(hz.id)) traces.set(hz.id, { g: geomOf(hz), startSec, activeMs: hz.activeMs })
}

/** 当たりが消えた後の残り (腕が崩れてかけらが舞い、影だまりが引く)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawVeilHandsLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  const sk = skinNow()
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + LINGER_MS + 160) { traces.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    if (sk) drawHandsAt(ctx, sk, tr.g, floorY, ms, tr.activeMs, timeSec)
  }
}

if (veilFx === 'hands' && typeof window !== 'undefined') preloadBrideVeilHandsAtlas()
